import type { GoogleMapsLibraries } from '@/core/service/googleMaps/initializer';

import { ErrorCategory } from '@/core/ports/datadog';
import { GoogleMapsAPI } from '@/core/service/googleMaps/api';
import { GoogleMapsInitializer } from '@/core/service/googleMaps/initializer';
import { useDatadog } from '~~/composables/useDatadog';

/**
 * Maps Google Maps library names to their corresponding types
 * @see https://developers.google.com/maps/documentation/javascript/libraries
 */
interface GoogleMapsLibraryTypes {
	core: google.maps.CoreLibrary;
	maps: google.maps.MapsLibrary;
	places: google.maps.PlacesLibrary;
	geocoding: google.maps.GeocodingLibrary;
	routes: google.maps.RoutesLibrary;
	marker: google.maps.MarkerLibrary;
	geometry: google.maps.GeometryLibrary;
	elevation: google.maps.ElevationLibrary;
	streetView: google.maps.StreetViewLibrary;
	journeySharing: google.maps.JourneySharingLibrary;
	drawing: google.maps.DrawingLibrary;
	visualization: google.maps.VisualizationLibrary;
}

type GoogleMapsLibraryMap = Partial<GoogleMapsLibraryTypes>;

interface GoogleMapsState {
	isInitializing: boolean;
	isInitialized: boolean;
	api: GoogleMapsLibraryMap | null;
	error: Error | null;
	promise: Promise<GoogleMapsLibraryMap | null> | null;
	initializedLibraries: GoogleMapsLibraries[] | null;
}

// Singleton state - shared across all composable instances
const state = reactive<GoogleMapsState>({
	isInitializing: false,
	isInitialized: false,
	api: null,
	error: null,
	promise: null,
	initializedLibraries: null
});

const DEFAULT_LIBRARIES: GoogleMapsLibraries[] = ['places', 'maps', 'marker'];

/**
 * Lazy-loading composable for Google Maps API
 *
 * Only initializes Google Maps when `init()` is called, not on page load.
 * Uses singleton pattern to ensure single initialization across the app.
 *
 * @example
 * // In a component that needs Google Maps
 * const { init, api, isReady } = useGoogleMaps();
 *
 * onMounted(async () => {
 *   await init();
 *   // Now api.value is available
 *   const { Place } = api.value.places;
 * });
 */
export function useGoogleMaps() {
	const runtimeConfig = useRuntimeConfig();
	const { trackError } = useDatadog();

	/**
	 * Initialize Google Maps API lazily
	 * Only loads the API when called, not on component mount
	 * Safe to call multiple times - subsequent calls return cached promise
	 *
	 * @returns The initialized API or null if initialization fails
	 */
	async function init(
		libraries: GoogleMapsLibraries[] = DEFAULT_LIBRARIES
	): Promise<GoogleMapsLibraryMap | null> {
		// Already initialized - return cached API if compatible
		if (state.isInitialized && state.api) {
			const allRequiredLibrariesAreLoaded = libraries.every(lib =>
				state.initializedLibraries?.includes(lib)
			);

			if (allRequiredLibrariesAreLoaded) {
				return state.api;
			}

			// Missing libraries - log to Datadog and return cached API with warning
			// We don't throw to avoid breaking components that depend on Google Maps
			const missingLibraries = libraries.filter(
				lib => !state.initializedLibraries?.includes(lib)
			);
			const errorMessage = `Google Maps API already initialized, but missing requested libraries: ${missingLibraries.join(', ')}`;

			trackError({
				message: errorMessage,
				source: 'source',
				category: ErrorCategory.Integration,
				fingerprint: 'google-maps-missing-libraries'
			});

			// Return cached API anyway - some functionality may still work
			// Components should handle null/undefined gracefully for missing libraries
			return state.api;
		}

		// Already initializing - return existing promise
		if (state.isInitializing && state.promise) {
			return state.promise;
		}

		// Start initialization
		state.isInitializing = true;
		state.error = null;

		state.promise = initializeGoogleMaps(libraries);

		try {
			const api = await state.promise;
			state.api = api;
			state.isInitialized = true;
			state.initializedLibraries = libraries;
			return api;
		} catch (error) {
			state.error = error as Error;

			trackError({
				message: `Failed to initialize Google Maps: ${(error as Error).message}`,
				source: 'source',
				category: ErrorCategory.Integration,
				stack: (error as Error).stack,
				fingerprint: 'google-maps-init-failed'
			});

			return null;
		} finally {
			state.isInitializing = false;
		}
	}

	async function initializeGoogleMaps(
		libraries: GoogleMapsLibraries[]
	): Promise<GoogleMapsLibraryMap | null> {
		const key = runtimeConfig.public.GOOGLE_MAPS_KEY;

		if (!key) {
			console.warn('Google Maps API key not configured');
			return null;
		}

		const initializer = new GoogleMapsInitializer({
			key,
			libraries
		});

		const googleMapsApi = new GoogleMapsAPI(initializer);
		return googleMapsApi.getLibraries();
	}

	/**
	 * Get a specific library, initializing if needed
	 *
	 * @example
	 * const places = await getLibrary('places');
	 * // places is typed as google.maps.PlacesLibrary | null
	 * const { Place } = places!;
	 */
	async function getLibrary<K extends GoogleMapsLibraries>(
		library: K
	): Promise<GoogleMapsLibraryTypes[K] | null> {
		const api = await init();
		return (api?.[library] as GoogleMapsLibraryTypes[K]) ?? null;
	}

	return {
		// Methods
		init,
		getLibrary,

		// Reactive state
		api: computed(() => state.api),
		isReady: computed(() => state.isInitialized && state.api !== null),
		isInitializing: computed(() => state.isInitializing),
		error: computed(() => state.error)
	};
}
